import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { AlertTriangle } from 'lucide-react'
import { useAuthStore } from '../services/authStore'
import legalService from '../services/legalService'
import Button from '../components/ui/Button'
import Card from '../components/ui/Card'
import { formatDate } from '../utils/date'

const LEGAL_DOC_LABELS = {
  terms_of_service: 'Terms of Service',
  privacy_policy: 'Privacy Policy',
}

function toReadableType(documentType) {
  if (!documentType) return 'Legal Document'
  return LEGAL_DOC_LABELS[documentType] || documentType.split('_').map((word) => word[0].toUpperCase() + word.slice(1)).join(' ')
}

export default function LegalAcceptance() {
  const navigate = useNavigate()
  const { legalRequiredDocs, refreshLegalRequired, logout } = useAuthStore()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLoadingDetails, setIsLoadingDetails] = useState(false)
  const [error, setError] = useState('')
  const [acceptedDocs, setAcceptedDocs] = useState({})
  const [grantOptionalConsent, setGrantOptionalConsent] = useState(false)
  const [currentDocs, setCurrentDocs] = useState([])

  const docs = useMemo(
    () => (Array.isArray(legalRequiredDocs) ? legalRequiredDocs : []),
    [legalRequiredDocs]
  )
  const allRequiredAccepted = docs.every((doc) => Boolean(acceptedDocs[doc.documentVersionId]))
  const docMetaByType = useMemo(() => {
    const map = new Map()
    currentDocs.forEach((doc) => {
      if (doc?.type && doc?.current) {
        map.set(doc.type, doc.current)
      }
    })
    return map
  }, [currentDocs])

  useEffect(() => {
    let isMounted = true
    const loadCurrentDocuments = async () => {
      setIsLoadingDetails(true)
      try {
        const items = await legalService.getCurrentDocuments()
        if (isMounted) {
          setCurrentDocs(Array.isArray(items) ? items : [])
        }
      } catch (_error) {
        if (isMounted) {
          setCurrentDocs([])
        }
      } finally {
        if (isMounted) {
          setIsLoadingDetails(false)
        }
      }
    }
    loadCurrentDocuments()
    return () => {
      isMounted = false
    }
  }, [])

  const acceptAll = async () => {
    if (docs.length === 0) {
      navigate('/', { replace: true })
      return
    }
    setIsSubmitting(true)
    setError('')
    try {
      for (const doc of docs) {
        await legalService.acceptDocument(doc.documentVersionId, 'web')
      }
      if (grantOptionalConsent) {
        await legalService.grantConsent('marketing_updates')
      } else {
        await legalService.withdrawConsent('marketing_updates')
      }
      const stillRequired = await refreshLegalRequired()
      if (!stillRequired.length) {
        navigate('/', { replace: true })
      }
    } catch (submitError) {
      const errorMessage = submitError?.response?.data?.message || submitError?.message || 'Unable to record acceptance.'
      setError(errorMessage)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      <Card className="p-6">
        <div className="flex items-start gap-3 mb-4">
          <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5" />
          <div>
            <h1 className="text-xl font-semibold text-foreground">Legal Update Required</h1>
            <p className="text-sm text-muted-foreground mt-1">
              You must accept the latest legal documents before continuing.
            </p>
          </div>
        </div>

        {error ? (
          <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            {error}
          </div>
        ) : null}

        <div className="space-y-2 mb-6">
          {docs.map((doc) => (
            <div key={doc.documentVersionId} className="rounded-md border border-outline p-3">
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={Boolean(acceptedDocs[doc.documentVersionId])}
                  onChange={(event) =>
                    setAcceptedDocs((prev) => ({ ...prev, [doc.documentVersionId]: event.target.checked }))
                  }
                />
                <span className="flex-1">
                  <p className="text-sm font-medium text-foreground">{toReadableType(doc.documentType)}</p>
                  <p className="text-xs text-muted-foreground">Version: {doc.version}</p>
                  {docMetaByType.get(doc.documentType)?.effectiveFrom ? (
                    <p className="text-xs text-muted-foreground">
                      Effective:{' '}
                      {formatDate(docMetaByType.get(doc.documentType).effectiveFrom)}
                    </p>
                  ) : null}
                  {docMetaByType.get(doc.documentType)?.changeSummary ? (
                    <p className="text-xs text-muted-foreground mt-1">
                      {docMetaByType.get(doc.documentType).changeSummary}
                    </p>
                  ) : null}
                </span>
              </label>
            </div>
          ))}
        </div>

        <div className="rounded-md border border-outline p-3 mb-6">
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              className="mt-1"
              checked={grantOptionalConsent}
              onChange={(event) => setGrantOptionalConsent(event.target.checked)}
            />
            <span>
              <p className="text-sm font-medium text-foreground">Optional consent: Product updates</p>
              <p className="text-xs text-muted-foreground">
                You can change this later in profile. This is not required to use the app.
              </p>
            </span>
          </label>
        </div>

        <div className="mb-6">
          <Link to="/legal/documents" className="text-sm text-blue-600 hover:text-blue-700">
            Review full legal documents
          </Link>
          {isLoadingDetails ? <p className="text-xs text-muted-foreground mt-1">Loading document details...</p> : null}
        </div>

        <div className="flex gap-3">
          <Button onClick={acceptAll} disabled={isSubmitting || docs.length === 0 || !allRequiredAccepted}>
            {isSubmitting ? 'Accepting...' : 'Accept and Continue'}
          </Button>
          <Button variant="secondary" onClick={logout} disabled={isSubmitting}>
            Logout
          </Button>
        </div>
      </Card>
    </div>
  )
}

